import React, { useEffect, useRef, useState } from 'react';
import { API_URL, buildKioskAuthHeaders, getStoredAuthToken } from './api';
import './KdsAlertSettings.css';

const FIELDS = [
  ['warnThresholdSeconds', 'Seuil orange (secondes)', 30, 3600, 'Délai avant le niveau d’avertissement.'],
  ['lateThresholdSeconds', 'Seuil rouge (secondes)', 60, 7200, 'Délai avant le niveau critique, au moins 30 secondes après le seuil orange.'],
];
const KEYS = [...FIELDS.map(([key]) => key), 'soundEnabled'];
function validSettings(settings) {
  return settings && FIELDS.every(([key, , min, max]) => Number.isInteger(settings[key]) && settings[key] >= min && settings[key] <= max)
    && settings.lateThresholdSeconds >= settings.warnThresholdSeconds + 30 && typeof settings.soundEnabled === 'boolean';
}

function parsePayload(payload) {
  if (payload?.schemaVersion !== 1 || !/^[a-f0-9]{64}$/.test(payload.revision || '') || !validSettings(payload.settings)) {
    throw new Error('Les paramètres reçus sont invalides. Rechargez-les avant de modifier.');
  }
  return { revision: payload.revision, settings: Object.fromEntries(KEYS.map(key => [key, payload.settings[key]])) };
}

const SESSION_MESSAGE = 'Votre session a changé. Rechargez la page avant de modifier les paramètres.';

export default function KdsAlertSettings({ allowed = false }) {
  const [open, setOpen] = useState(false);
  const [baseline, setBaseline] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [conflict, setConflict] = useState(false);
  const [sessionChanged, setSessionChanged] = useState(false);
  const [confirmReload, setConfirmReload] = useState(false);
  const ownerToken = useRef(null), request = useRef(0), controller = useRef(null), heading = useRef(null), allowedRef = useRef(allowed);
  allowedRef.current = allowed;
  const dirty = Boolean(draft && baseline && KEYS.some(key => String(draft[key]) !== String(baseline.settings[key])));

  useEffect(() => () => { request.current += 1; controller.current?.abort(); }, []);
  useEffect(() => { if (open) heading.current?.focus(); }, [open]);

  function checkSession() {
    if (!ownerToken.current || getStoredAuthToken() !== ownerToken.current) {
      setSessionChanged(true);
      throw new Error(SESSION_MESSAGE);
    }
  }

  async function transact(method, settings) {
    let sequence;
    let sent = false, definitiveFailure = false;
    try {
      checkSession();
      if (!allowedRef.current) throw new Error('Votre accès ne permet plus de modifier ces paramètres.');
      sequence = ++request.current;
      controller.current?.abort();
      controller.current = new AbortController();
      setBusy(true); setError(''); setMessage(''); setConfirmReload(false);
      const headers = buildKioskAuthHeaders();
      if (method === 'PUT') headers.set('Content-Type', 'application/json');
      sent = true;
      const response = await fetch(`${API_URL}/api/admin/kds-alert-settings`, {
        method, headers, cache: 'no-store', signal: controller.current.signal,
        ...(method === 'PUT' ? { body: JSON.stringify({ revision: baseline.revision, settings }) } : {}),
      });
      const payload = await response.json().catch(() => ({}));
      if (sequence !== request.current) return;
      checkSession();
      if (!allowedRef.current) throw new Error('Votre accès ne permet plus de modifier ces paramètres.');
      if (!response.ok) {
        definitiveFailure = response.status >= 400 && response.status < 500;
        if (response.status === 401 || response.status === 403) {
          setSessionChanged(true);
          throw new Error('Votre accès aux paramètres a été refusé. Votre saisie est conservée. Rechargez la page pour vérifier votre session.');
        }
        if (response.status === 409 && payload.code === 'KDS_ALERT_SETTINGS_CONFLICT') {
          setConflict(true);
          throw new Error('Les paramètres ont été modifiés ailleurs. Votre saisie est conservée. Rechargez les paramètres avant de les modifier à nouveau.');
        }
        throw new Error(typeof payload.error === 'string' ? payload.error : 'Impossible de vérifier les paramètres. Votre saisie est conservée.');
      }
      const next = parsePayload(payload);
      setBaseline(next); setDraft(next.settings); setConflict(false);
      setMessage(method === 'PUT' ? 'Paramètres enregistrés.' : 'Paramètres actualisés.');
    } catch (failure) {
      if (sequence !== undefined && sequence !== request.current) return;
      if (method === 'PUT' && sent && !definitiveFailure) setConflict(true);
      setError(failure instanceof TypeError ? 'Réponse non reçue. Votre saisie est conservée. Rechargez les paramètres pour vérifier leur état avant un nouvel enregistrement.' : failure.message || 'Impossible de vérifier les paramètres. Votre saisie est conservée.');
    } finally {
      if (sequence === undefined || sequence === request.current) setBusy(false);
    }
  }

  function show() {
    setOpen(true);
    if (ownerToken.current === null) ownerToken.current = getStoredAuthToken();
    if (!baseline) transact('GET');
  }

  function save(event) {
    event.preventDefault();
    if (busy || conflict || sessionChanged || !allowed || !baseline) return;
    const settings = { ...Object.fromEntries(FIELDS.map(([key]) => [key, draft[key] === '' ? NaN : Number(draft[key])])), soundEnabled: draft.soundEnabled };
    if (!validSettings(settings)) { setError('Vérifiez les seuils entiers : orange de 30 à 3 600 secondes, rouge de 60 à 7 200 secondes et au moins 30 secondes après le seuil orange.'); return; }
    transact('PUT', settings);
  }

  if (!open) return allowed ? <button type="button" className="kds-alert-settings-button is-secondary kds-alert-settings-open" onClick={show}>Paramètres des alertes KDS</button> : null;

  return <section className="kds-alert-settings" aria-labelledby="kds-alert-settings-title">
    <div className="kds-alert-settings-head"><h3 id="kds-alert-settings-title" ref={heading} tabIndex={-1}>Paramètres des alertes KDS</h3><button type="button" className="kds-alert-settings-button is-secondary" disabled={busy} onClick={() => setOpen(false)}>Fermer les paramètres</button></div>
    <p>Ces réglages s’appliquent aux alertes cuisine de cet établissement. Les sons existants accompagnent les nouvelles commandes et les retards critiques ; aucun son n’est joué depuis ce panneau.</p>
    {!allowed && <p role="alert">Votre accès ne permet plus de modifier ces paramètres. Votre saisie est conservée.</p>}
    {error && <p className="kds-alert-settings-error" role="alert">{error}</p>}
    {message && <p role="status">{message}</p>}
    {busy && <p role="status">Vérification des paramètres…</p>}
    {draft && <form onSubmit={save}>
      <fieldset disabled={busy || !allowed || sessionChanged}>
        <legend className="product-options-sr-only">Réglages des alertes KDS</legend>
        <div className="kds-alert-settings-grid">{FIELDS.map(([key, label, min, max, help]) => <label key={key}><span>{label}</span><input aria-label={label} aria-describedby={`kds-alert-setting-${key}-help`} type="number" required min={min} max={max} step={1} value={draft[key]} onChange={event => { setDraft(current => ({ ...current, [key]: event.target.value })); setMessage(''); setConfirmReload(false); }} /><small id={`kds-alert-setting-${key}-help`}>{help}</small></label>)}</div>
        <label className="kds-alert-settings-sound"><input type="checkbox" checked={draft.soundEnabled} onChange={event => { setDraft(current => ({ ...current, soundEnabled: event.target.checked })); setMessage(''); setConfirmReload(false); }} /><span>Alertes sonores cuisine</span></label>
        <div className="kds-alert-settings-actions"><button type="submit" className="kds-alert-settings-button" disabled={!dirty || conflict}>Enregistrer les paramètres</button><button type="button" className="kds-alert-settings-button is-secondary" disabled={!dirty} onClick={() => { setDraft(baseline.settings); setMessage('Modifications locales annulées.'); setConfirmReload(false); }}>Annuler les modifications</button></div>
      </fieldset>
    </form>}
    <div className="kds-alert-settings-actions"><button type="button" className="kds-alert-settings-button is-secondary" disabled={busy || !allowed || sessionChanged} onClick={() => dirty ? setConfirmReload(true) : transact('GET')}>Recharger les paramètres</button></div>
    {confirmReload && <div className="kds-alert-settings-confirm" role="group" aria-label="Confirmer le rechargement"><p>Recharger remplacera votre saisie par les paramètres enregistrés sur le serveur.</p><button type="button" className="kds-alert-settings-button is-secondary" onClick={() => transact('GET')}>Recharger et remplacer ma saisie</button><button type="button" className="kds-alert-settings-button is-secondary" onClick={() => setConfirmReload(false)}>Conserver ma saisie</button></div>}
  </section>;
}
